import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useHistory, useParams } from 'react-router';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { route } from '~/app/helpers/route';
import { notify } from '~/app/helpers/notify';
import Icon from '~/app/common/Icon';
import Loader from '~/app/common/Loader';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import TextDialog from '~/app/common/TextDialog';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { useStoreActions, useStoreState } from '~/store';
import Attachment from './Attachment';
import { WORKORDER_ROUTES } from '~/workorder/Router';
import { WorkOrderStatuses, WorkOrderTaskIssueStatus, WorkOrderTaskServiceStatus } from '~/workorder/types';
import classes from './style.module.scss';
import footerClasses from '~/workorder/Detail/Footer/style.module.scss';
import Users from './Users';
import Parts from './Parts';
import Materials from './Material';
import Checklist from './Checklist';
import RemoveTaskDialog from './RemoveTaskDialog';

export type DetailComponent = React.FC;

const Detail: DetailComponent = () => {
    
    const { id, taskId, type } = useParams<{ id: string; taskId: string; type: string }>();

    const history = useHistory();
    const { setData } = useAppHeader();
    const { t } = useTranslation('workorder');
    const { t: tIssue } = useTranslation('issue');
    const { t: tService } = useTranslation('service');

    const fetching = useStoreState(state => state.workOrder.fetching);
    const taskData = useStoreState(state => state.workOrder.taskData);
    const workOrderStatus = useStoreState(state => state.workOrder.data?.status);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const getWorkOrderTaskDetail = useStoreActions(actions => actions.workOrder.getWorkOrderTaskDetail);
    const getWorkOrderDetail = useStoreActions(actions => actions.workOrder.getWorkOrderDetail);
    const removeWorkOrderTask = useStoreActions(actions => actions.workOrder.removeWorkOrderTask);
    const workOrderId = useStoreState(state => state.workOrder.data.id);

    const [isFabOpen, setIsFabOpen] = useState(false);
    const [isTimeBlockOpen, setIsTimeBlockOpen] = useState(false);
    const [isPartsConfirmOpen, setIsPartsConfirmOpen] = useState(false);
    const [isSimpleConfirmOpen, setIsSimpleConfirmOpen] = useState(false);
    const [removing, setRemoving] = useState(false);

    const typeLabel = t(`detail.taskType.${taskData.type}`);
    const isIssue = taskData.type === IssueOrServiceType.ISSUE;

    const descriptionBody = useMemo(() => {
        const text = taskData.description?.trim();
        return text || '-';
    }, [taskData.description]);

    const users = useMemo(() => taskData.users ?? [], [taskData.users]);
    const parts = useMemo(() => taskData.parts ?? [], [taskData.parts]);
    const materials = useMemo(() => taskData.materials ?? [], [taskData.materials]);

    const canRemoveTask = useMemo(() => {
        return !(
            netWorkStatus !== 0 ||
            workOrderStatus === WorkOrderStatuses.APPROVED ||
            workOrderStatus === WorkOrderStatuses.DELAYED ||
            workOrderStatus === WorkOrderStatuses.ONHOLD ||
            (taskData.type === IssueOrServiceType.ISSUE && taskData.status !== WorkOrderTaskIssueStatus.DURING_WORK) ||
            (taskData.type === IssueOrServiceType.SERVICE && taskData.status !== WorkOrderTaskServiceStatus.DURING_WORK)
        );
    }, [netWorkStatus, workOrderStatus, taskData.status, taskData.type]);

    const pageTitle = taskData.title || t('detail.tasks');
    usePageTitle(pageTitle);

    useEffect(() => {
        if (taskId) {
            const typeParam = type === 'issue' ? IssueOrServiceType.ISSUE : IssueOrServiceType.SERVICE;
            getWorkOrderTaskDetail({ id: Number(id), taskId: Number(taskId), type: typeParam });
        }
    }, [taskId, getWorkOrderTaskDetail, id, type]);

    useEffect(() => {
        if (!!!workOrderId) {
            getWorkOrderDetail({ id: Number(id) });
        }
    }, [getWorkOrderDetail, workOrderId, id]);

    useEffect(() => {
        setData({
            title: pageTitle,
            subTitle: typeLabel.toUpperCase(),
            backLink: history.length > 1 ? history.goBack : route(WORKORDER_ROUTES.detail, { id }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            notification: false,
            search: false,
        });
    }, [setData, id, pageTitle, typeLabel, history.length, history.goBack]);

    const executeRemove = useCallback((deleteParts: boolean) => {
        if (removing) {
            return;
        }

        const typeParam = type === 'issue' ? IssueOrServiceType.ISSUE : IssueOrServiceType.SERVICE;
        setRemoving(true);
        setIsFabOpen(false);
        setIsPartsConfirmOpen(false);
        setIsSimpleConfirmOpen(false);

        removeWorkOrderTask({
            workOrderId: Number(id),
            taskId: Number(taskId),
            deleteParts,
            type: typeParam,
        })
            .then(response => {
                if (response.success) {
                    notify(t('detail.taskRemoved'), 'success');
                    return getWorkOrderDetail({ id: Number(id), showLoading: false }).then(() => {
                        history.push(route(WORKORDER_ROUTES.detail, { id }));
                    });
                }
                notify(t('detail.removeTaskError'), 'error');
            })
            .catch(() => {
                notify(t('detail.removeTaskError'), 'error');
            })
            .finally(() => {
                setRemoving(false);
            });
    }, [removing, type, removeWorkOrderTask, id, taskId, t, getWorkOrderDetail, history]);

    const handleRemoveClick = useCallback(() => {
        setIsFabOpen(false);

        if (users.length > 0) {
            setIsTimeBlockOpen(true);
            return;
        }

        if (parts.length > 0 || materials.length > 0) {
            setIsPartsConfirmOpen(true);
            return;
        }

        setIsSimpleConfirmOpen(true);
    }, [users.length, parts.length, materials.length]);

    if (fetching) {
        return <Loader size="32" />;
    }

    return (
        <>
            <div className={classNames(classes.page, {
                [classes['fab-open']]: isFabOpen,
                [classes['has-fab']]: canRemoveTask,
            })}>
                <div className={classNames(classes.card, classes['header-card'])}>
                    <div className={classes['header-main']}>
                        <div
                            className={classNames(
                                classes['type-icon-wrap'],
                                isIssue ? classes.issue : classes.service,
                            )}
                        >
                            <Icon name={isIssue ? 'warning' : 'build'} />
                        </div>
                        <div className={classes['header-text']}>
                            <p className={classes['type-label']}>{typeLabel}</p>
                            <h1 className={classes.title}>{taskData.title}</h1>
                        </div>
                    </div>
                    <span className={classNames(classes['status-badge'], taskData.type === IssueOrServiceType.ISSUE ? classes[`issue-${taskData.status}`] : classes[`service-${taskData.status}`])}>
                        {taskData.type === IssueOrServiceType.ISSUE ? tIssue(`statuses.${taskData.status}`) : tService(`statuses.${taskData.status}`)}
                    </span>
                </div>

                <div className={classes.card}>
                    <h2 className={classes['section-title']}>{t('detail.description')}</h2>
                    <p className={classes['description-body']}>{descriptionBody}</p>
                </div>

                {taskData.type === IssueOrServiceType.SERVICE && <Checklist />}

                <Users users={users} taskStatus={taskData.status} />

                <Parts parts={parts} />

                <Materials materials={materials} />

                <Attachment taskData={taskData} workOrderId={workOrderId} />
            </div>

            {canRemoveTask && (
                <div className={footerClasses.fabRoot}>
                    {isFabOpen && (
                        <div className={footerClasses.stack} role="menu">
                            <div className={footerClasses.actionRow} role="none">
                                <span className={footerClasses.label}>{t('detail.removeTask')}</span>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={classNames(footerClasses.actionButton, footerClasses.actionButtonRed)}
                                    aria-label={t('detail.removeTask')}
                                    onClick={handleRemoveClick}
                                    disabled={removing}
                                >
                                    <Icon name="delete" size={22} />
                                </button>
                            </div>
                        </div>
                    )}
                    <button
                        type="button"
                        className={footerClasses.toggleFab}
                        aria-expanded={isFabOpen}
                        onClick={() => setIsFabOpen(!isFabOpen)}
                    >
                        <Icon name={isFabOpen ? 'close' : 'touch_app'} size={28} />
                    </button>
                </div>
            )}

            <TextDialog
                isOpen={isTimeBlockOpen}
                onClose={() => setIsTimeBlockOpen(false)}
                text={t('detail.removeTaskTimeEntriesBlock')}
            />

            <RemoveTaskDialog
                isOpen={isPartsConfirmOpen}
                onKeepParts={() => executeRemove(false)}
                onRemoveParts={() => executeRemove(true)}
                onCancel={() => setIsPartsConfirmOpen(false)}
            />

            <ConfirmationMenu
                isOpen={isSimpleConfirmOpen}
                onConfirm={() => executeRemove(false)}
                onCancel={() => setIsSimpleConfirmOpen(false)}
                confirmationText={t('detail.removeTaskConfirm')}
                confirmLabel={t('detail.removeTaskYes')}
                cancelLabel={t('detail.removeTaskNo')}
            />
        </>
    );
};

export default Detail;
